import { BEIGE_600, GRAY_1000 } from 'constants/styling/theme'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { Fragment, useCallback, useState } from 'react'

import { CircleIcon } from '../CircleIcon'
import { MUIButton } from '../MUIButton'
import { MUIDivider } from '../MUIDivider'
import { MultiEmailInput } from '../MultiEmailInput'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import { useTranslation } from 'react-i18next'

/** @interface Props for the EmailForwardning component. */
interface Props {
  /** List of emails to be forwarded. */
  ccEmails: string[]
  /** Handle action click triggered after the emails is added */
  onChange: (emails: string[]) => void
  /** Handle action click triggered for deleting. */
  onDelete: (email: string) => void
  /** Whether the component is disabled (e.g. while a mutation is in-flight). */
  disabled?: boolean
  /** Whether a mutation is currently loading (cover the email list with an overlay). */
  isLoading?: boolean
}

/**
 * @component
 * Emails forwarding component.
 * 
 * @example
 * <EmailForwardning
 *   ccEmails={ccEmails}
 *   onChange={() => console.log('updated')}
 *   onDelete={() => console.log('removed')}
 * />
 */
export const EmailForwardning: React.FC<Props> = ({
  ccEmails,
  onChange,
  onDelete,
  disabled,
  isLoading
}) => {
  const { t } = useTranslation(['email_forwarding', 'common'])
  const [emails, setEmails] = useState<string[]>([])

  const handleAddEmails = useCallback(() => {
    onChange([...ccEmails, ...emails])
    setEmails([])
  }, [ccEmails, emails, onChange])

  return (
    <>
      <Stack flexDirection="row" gap="2rem" justifyContent="flex-start" alignItems="flex-start" paddingTop=".4rem">

        <Box width="100%">
          {/* W2.4 (BX-F09): inputAriaLabel gives the email text input a
              programmatic label. The SectionedBorderBox title "Email forwarding"
              is a visual section label, not a <label htmlFor> association, so
              screen readers announced the field without any label. */}
          <MultiEmailInput
            emails={emails}
            autofocus={false}
            withHint={false}
            previouslyAddedEmails={ccEmails}
            placeholder={t('placeholder')}
            inputAriaLabel={t('cc_email_input_label')}
            onChange={(emails) => setEmails(emails)}
          />
        </Box>

        <Box paddingTop=".6rem" flexShrink={0}>
          <MUIButton
            onClick={handleAddEmails}
            disabled={!emails.length || disabled}
          >
            {t('add')}
          </MUIButton>
        </Box>
      </Stack>

      <Box position="relative">
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={1}
          >
            <CircularProgress size="2.4rem" />
          </Box>
        )}

        <Stack
          gap='.4rem'
          justifyContent="flex-start"
          paddingTop=".4rem"
          sx={{
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            transition: 'opacity 0.2s ease',
          }}
        >
          {ccEmails.map((email, index) => (
            <Fragment key={email}>
              <Stack
                padding="1.4rem 0"
                flexDirection="row"
                gap=".2rem"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack flexDirection="row" gap=".8rem" alignItems="center">
                  <CircleIcon
                    circleColor={BEIGE_600}
                    size='2.8rem'
                    icon={<PersonOutlinedIcon sx={{ color: GRAY_1000 }} />}
                  />
                  <Typography variant="text-md" fontWeight={550} color={GRAY_1000}>{email}</Typography>
                </Stack>

                <MUIButton
                  type="orangeSecondaryNoBorder"
                  onClick={() => onDelete(email)}
                >
                  {t('common:Remove')}
                </MUIButton>
              </Stack>

              {index !== ccEmails.length - 1 && <MUIDivider margin={2} />}
            </Fragment>
          ))}
        </Stack>
      </Box>
    </>
  )
}
